import { SORT_STATE } from '@abb/common-ux';
import { RequestLogFilterValue } from '@module/analysis/RequestLogFilter';
import { Moment } from 'moment';

export enum AntivirusFindings {
  Unknown = 0,
  FileToLarge = 1,
  FileNameToLong = 2,
  InvalidFileType = 3,
  Clean = 4,
  VirusDetected = 5 
}

export interface IPartUploadValidationResult {
  upload: ISparePartsUpload; 
  UploadedParts: ISparePart[];
  Errors: string[];
  UploadResultStatus: IUploadResult;
  status: string;
  statusText: string;
}

export interface IPartUploadValidationResponse {
  uploadResult?: IPartUploadValidationResult;
  antivirusFindings: number;
}

export interface ISparePartsUpload {
  sparePartsUploadID: string;
  filename: string;
  createdAt?: Moment;
}

export interface ISparePart {
  PartNumber: string;
  Description: string;
  Quantity: string;
  Matched: string;
}

export interface IUploadResult {
  SparePartsUploadID: string;
  TotalParts: number;
  MatchedParts: number;
  UnMatchedParts: number;
}

export interface IAnalysisContactForm {
  requestID: string;
  contactName: string; 
  contactEmail: string;
  confirmContactEmail: string;
  companyName: string;
  location: string;
  country: string;
  phoneNumber: string; 
  postalCode: string;
  condition: string; 
  downtime: string; 
  storage: string; 
  awareness: string; 
}

export class ContactForm implements IAnalysisContactForm {
  requestID: string;
  contactName: string;
  contactEmail: string; 
  confirmContactEmail: string;
  companyName: string;
  location: string;
  country: string;
  phoneNumber: string; 
  postalCode: string;
  condition: string; 
  downtime: string; 
  storage: string; 
  awareness: string; 

  constructor() {
    this.requestID = '';
    this.contactName = '';
    this.contactEmail = '';
    this.confirmContactEmail = '';
    this.companyName = '';
    this.location = '';
    this.country = '';
    this.phoneNumber = ''; 
    this.postalCode = ''; 
    this.condition = ''; 
    this.downtime = ''; 
    this.storage = ''; 
    this.awareness = '';
  }
}

export interface IRequestReport {
  sparePartsUploadDetailsID: string;
  requestID: string;
  createdAt: Date;
  contactEmail: string;
  companyName: string;
  country: string;
  leadGenerated: boolean;
  partsSpecialistName: string;
  status: string;
  comments: string;
  associatedSite: string;
  serviceProSiteId?: string;
}

export interface IRequestReportRequest {
  requestFilter: RequestLogFilterValue;
  sortField: string;
  sortDirection: SORT_STATE;
  pageSize: number;
  currentPage: number;
}

export interface IRequestReportResponse {
  requestLogs: IRequestReport[];
  totalRecords: number;
}

export interface IRequestReportDetails {
  sparePartsUploadDetailsID: string;
  requestID: number;
  sparePartsUploadID: string;
  leadGenerated: boolean;
  contactName: string;
  contactEmail: string;
  companyName: string;
  phoneNumber: string;
  location: string;
  postalCode: string;
  country: string;
  condition: string;
  downtime: string;
  storage: string; 
  awareness: string; 
  uploaded?: string;
  partsSpecialistName: string;
  partsSpecialistEmail: string;
  statusName: string; 
  comments: string; 
  matchedParts: IMatchedSpareParts[];
  unMatchedParts: ISpareParts[];
  associatedSite?: ISite;
  siteId?: string;
  inventorySource?: string;
  hasExistingParts: boolean;
  inventoryUploaded: boolean;
}

export interface IAssignedSite {
  servIsId?: string;
  hasExistingParts: boolean;
  inventorySource?: string;
}

export interface ISpareParts {
  partNumber: number;
  description: string;
  quantity: number;
}

export interface IMatchedSpareParts extends ISpareParts {
  riskLevel: string;
  lifeCycle: string;
  countryStock: string;
  replaceable: string;
  repairable: string;
  replacementPartNumber: string;
}

export interface IParts {
  matchedSpareParts: IMatchedSpareParts[];
  unmatchedSpareParts: ISpareParts[];
}

export class RequestReportDetails implements IRequestReportDetails {
  public sparePartsUploadDetailsID: string;
  public sparePartsUploadID: string;
  public requestID: number;
  public leadGenerated: boolean;
  public contactName: string;
  public contactEmail: string;
  public companyName: string;
  public phoneNumber: string;
  public location: string;
  public postalCode: string;
  public country: string;
  public condition: string;
  public downtime: string;
  public storage: string;
  public awareness: string;
  public uploaded?: string;
  public partsSpecialistName: string;
  public partsSpecialistEmail: string;
  public statusName: string;
  public comments: string;
  matchedParts: IMatchedSpareParts[];
  unMatchedParts: ISpareParts[];
  public siteId?: string;
  public hasExistingParts: boolean;
  public inventoryUploaded: boolean;

  constructor() {
    this.sparePartsUploadDetailsID = '';
    this.sparePartsUploadID = '';
    this.requestID = 0;
    this.leadGenerated = false;
    this.contactName = '';
    this.contactEmail = '';
    this.companyName = '';
    this.phoneNumber = '';
    this.location = '';
    this.postalCode = '';
    this.country = '';
    this.condition = '';
    this.downtime = '';
    this.storage = '';
    this.awareness = '';
    this.uploaded = '';
    this.partsSpecialistName = '';
    this.partsSpecialistEmail = '';
    this.statusName = '';
    this.matchedParts = [];
    this.unMatchedParts = [];
    this.siteId = '';
    this.hasExistingParts = false;
    this.inventoryUploaded = false;
  }

}

export interface ISite {
  servIsId: string;
  siteName: string;
  ownerId: string;
  ownerName: string;
  operatorId: string;
  operatorName: string;
  city: string;
  country: string;
  region: string;
  serviceProSiteId?: string;
}

export interface ISubmitRequestResult {
  siteId: string;
  inventoryUploaded: boolean;
}

export enum ACCEPTED_TYPES {
  XLS = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}

export interface ICountry {
  countryID: string;
  countryName: string;
}

export interface IUserProfile {
  aio: any;
  at_hash: any;
  aud: any;
  exp: any;
  iat: any;
  iss: any;
  name: string;
  nbf: any;
  nonce: any;
  oid: any;
  preferred_username: any;
  sub: any;
  tid: any;
  uti: any;
  ver: any;
}

export interface IUserToken {
  access_token: any;
  expires_at: any;
  id_token: any;
  profile: IUserProfile;
  scope: any;
  session_state: any;
  state: any;
  token_type: any;
  expired: boolean;
  expires_in: any;
  scopes: any;
}

export interface IUserPermissions {
  email: string;
  permissions: IUserPermission[];
}

export interface IUserPermission {
  code: string;
  isScoped: boolean;
  scopes: IPermissionScope[];
}

export interface IPermissionScope {
  scopeName: string;
  isAllValues: boolean;
  scopeValues: string[];
}

export enum PermissionConstants {
  AdministerPlus = 'PIADMPL',
  AdministerPartInventory = 'PIADMPI',
  ManagePartInventory = 'PIMNGPI'
}
